import { useListColor } from '@aibees/elephant/components/action-bulk-select-color-label/config';
import {
  LabelGrouping,
  LabelType,
  LabelVisibility,
} from '@aibees/elephant/types/label';
import { Icon, LockIcon, TextEllipsis } from '@aibees/shared/components';
import { classnames, fHumanReadableNumber } from '@aibees/shared/utils';
import { Box, Checkbox, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { LabelsListItemProps as Props } from './types';

export const LabelsListItem = memo(
  ({ className, label, checked, onChange }: Props) => {
    const isPeople = useMemo(
      () => label.grouping === LabelGrouping.PEOPLE,
      [label.grouping]
    );
    const isPrivate = useMemo(
      () => label.visibility === LabelVisibility.PRIVATE,
      [label.visibility]
    );
    const isLabel = useMemo(() => label.type === LabelType.LABEL, [label.type]);
    const listColor = useListColor(label.grouping);

    const iconName = useMemo(
      () => (isPeople ? 'people-outline' : 'business'),
      [isPeople]
    );
    const iconColor = useMemo(
      () => (isLabel ? label.color : listColor),
      [label.color, listColor, isLabel]
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event.target.checked, label);
    };

    return (
      <Styled.Wrapper
        className={classnames('LabelsListItem', className)}
        sx={SX.root}
      >
        <Checkbox checked={checked} onChange={handleChange} size="small" />

        <Box sx={SX.labelWrapper}>
          <Icon
            name={iconName}
            size={18}
            color={iconColor}
            sxx={{ flexShrink: 0 }}
          />

          <TextEllipsis variant="body4" fontWeight={400} color="grey.700">
            {label.name}
          </TextEllipsis>
        </Box>

        <Box sx={SX.metadataWrapper}>
          {isPrivate && <LockIcon />}

          <Typography variant="body4" fontWeight={500} color="text.secondary">
            {fHumanReadableNumber(label.records)}
          </Typography>
        </Box>
      </Styled.Wrapper>
    );
  }
);

LabelsListItem.displayName = 'LabelsListItem';
